import { Ajax } from "../../Common/Ajax";
import { CookieHelper } from "../../Common/CookieHelper";
import { GlobalData } from "../../Common/GlobalData";
import { HeatMap } from "../../PersonalReports/HeatMap";
import { AccountHistory } from "./AccountHistory";
import { AccountList } from "./AccountList";
import { AccountManagerConsts } from "./AccountManagerConsts";
import { ReadyToBuyList } from "./ReadyToBuyList";
import { ReasonsToSell } from "./ReasonsToSell";
import { WatchList } from "./WatchList";

export class AccountManagerMetadata {
    public numStocks: number;
    public maxStocks: number;
    public sumCostBasis: number;
    public maxInvestment: number;
    public percentInvested: number;
    public portfolioAtrWithCash: number;
    public portfolioAtrWithoutCash: number;

    public constructor(metadata: any) {
        this.numStocks = metadata.numStocks;
        this.maxStocks = metadata.maxStocks;
        this.sumCostBasis = metadata.sumCostBasis;
        this.maxInvestment = metadata.maxInvestment;
        this.percentInvested = metadata.percentInvested;
        this.portfolioAtrWithCash = metadata.portfolioAtrWithCash;
        this.portfolioAtrWithoutCash = metadata.portfolioAtrWithoutCash;
    }
}

export class TabStrip {
    private static TAB_STRIP_ID = 'tabStrip';
    private static TAB_STRIP = '#' + TabStrip.TAB_STRIP_ID;
    private static ACCOUNT_PREFIX = 'XXXX-';

    private formatter!: Intl.NumberFormat;
    private metadata: AccountManagerMetadata | null = null;

    private watchListTree!: kendo.ui.DropDownTree;
    private watchList: WatchList | null = null;

    private static _instance: TabStrip;
    public static getInstance() {
        return this._instance || (this._instance = new this());
    }

    private constructor() {
        this.init();
    }

    public getSelectedIndex() {
        return $(TabStrip.TAB_STRIP).data("kendoTabStrip")!.select();
    }

    public getMetadata() {
        return this.metadata;
    }

    private init() {
        $(TabStrip.TAB_STRIP).kendoTabStrip({
            select: this.onSelect,
            activate: this.onActivate,
        });

        this.formatter = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            // These options are needed to round to whole numbers if that's what you want.
            //minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
            //maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
        });

        this.initHeaderData();
    }

    private onSelect() {
        const self = TabStrip.getInstance();
        if (self.watchList) {
            $(self.watchList.getGridId()).hide();
        }
        $(ReadyToBuyList.GRID).hide();
        $(ReasonsToSell.GRID).hide();
        $(AccountList.GRID).hide();
        $(AccountHistory.GRID).hide();
    }

    private onActivate(e: kendo.ui.TabStripActivateEvent) {
        const tabNum = e.contentElement?.id.substring(TabStrip.TAB_STRIP_ID.length + 1);
        switch (tabNum) {
            case '1':
                const self = TabStrip.getInstance();
                const watchListIds = self.getCheckedWatchListIds();

                if (self.watchList) {
                    self.watchList.destroy();
                }

                self.watchList = new WatchList();
                self.watchList.initWithWatchListIds(watchListIds);
                break;
            case '2':
                const readyToBuyList = ReadyToBuyList.getInstance();
                readyToBuyList.init();
                break;
            case '3':
                const reasonsToSell = ReasonsToSell.getInstance();
                reasonsToSell.init();
                break;
            case '4':
                const accountList = AccountList.getInstance();
                accountList.init();
                break;
            case '5':
                new HeatMap(true);
                break;
            case '6':
                const accountHistory = AccountHistory.getInstance();
                accountHistory.init();
                break;
        }
    }

    private initHeaderData() {
        const urlSuffix = GlobalData.URL_SUFFIX_PORTFOLIO_ACCOUNT_MANAGER_WATCHLIST +
            '/HeaderData?account=' + CookieHelper.get(GlobalData.PORTFOLIO_ACCOUNT_SELECTOR_COOKIE);

        const success = (data: any) => {
            this.initMetadata(data.metadata);
            this.initCheckboxes(data.watchListNames);
        };

        const error = () => {
            console.log('error');
        }

        Ajax.invoke(urlSuffix, 'GET', null, success, error);
    }

    private initMetadata(metadata: any) {
        this.metadata = new AccountManagerMetadata(metadata);

        $('#numStocks').html(metadata.numStocks + ' / ' + metadata.maxStocks);
        $('#sumCostBasis').html(this.formatter.format(metadata.sumCostBasis) + ' / ' +
            this.formatter.format(metadata.maxInvestment));
        $('#percentInvested').html(metadata.percentInvested + '%');
        $('#portfolioAtrWithCash').html(metadata.portfolioAtrWithCash + '%');
        $('#portfolioAtrWithoutCash').html(metadata.portfolioAtrWithoutCash + '%');
    }

    private compareWatchLists(a: any, b: any) {
        // We want the account number to appear on top of the sorted list.
        if (a.watchListName.startsWith(TabStrip.ACCOUNT_PREFIX)) {
            return -1;
        }
        if (b.watchListName.startsWith(TabStrip.ACCOUNT_PREFIX)) {
            return 1;
        }

        if (a.watchListName < b.watchListName) {
            return -1;
        }
        if (a.watchListName > b.watchListName) {
            return 1;
        }
        return 0;
    }

    private initCheckboxes(watchLists: any) {
        watchLists.sort(this.compareWatchLists);

        const dataSource: any[] = [];
        for (const row of watchLists) {
            dataSource.push({
                watchListId: row.watchListId,
                watchListName: row.watchListName,
                checked: row.watchListName.startsWith(TabStrip.ACCOUNT_PREFIX),
            });
        }

        this.watchListTree = $("#watchListTree").kendoDropDownTree({
            placeholder: "Select WatchLists ...",
            checkboxes: true,
            checkAll: true,
            autoClose: false,
            dataSource: dataSource,
            dataValueField: "watchListId",
            dataTextField: "watchListName",
        }).data('kendoDropDownTree')!;

        // When the user clicks the WatchList checkboxes refresh button, update the URL
        // and refresh the grid.
        $(AccountManagerConsts.WATCHLISTS_TREE_REFRESH).on('click', () => {
            const checkedWatchListIds = this.getCheckedWatchListIds();
            this.watchList?.updateDataSource(checkedWatchListIds);
        });
    }

    private getCheckedWatchListIds() {
        const checkedWatchLists = this.getCheckedWatchLists();
        const checkedWatchListIds: number[] = [];
        for (const watchList of checkedWatchLists) {
            checkedWatchListIds.push(watchList.watchListId);
        }

        return checkedWatchListIds;
    }

    private getCheckedWatchLists() {
        const nodes = this.watchListTree.dataSource.view();
        return this.getCheckedNodes(nodes);
    }

    private getCheckedNodes(nodes: any) {
        let node, childCheckedNodes;
        let checkedNodes: any[] = [];
        for (let i = 0; i < nodes.length; i++) {
            node = nodes[i];
            if (node.checked) {
                checkedNodes.push(node);
            }
            else {
                if (node.hasChildren) {
                    childCheckedNodes = this.getCheckedNodes(node.children.view());
                    if (childCheckedNodes.length > 0) {
                        checkedNodes = checkedNodes.concat(childCheckedNodes);
                    }
                }
            }
        }

        return checkedNodes;
    }
}

